import React, { useState } from 'react';
import {
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Phone,
  Mail,
  User,
  Building2,
  MessageSquare,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCreateDemoRequestMutation } from '../../store/vendorApi';
import type { CreateDemoRequestDto } from '@clareefai/types';

interface DemoRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendorSlug: string;
  source: 'general' | 'review';
  reviewId?: string;
  promoterId?: string;
  promoterName?: string;
  buttonText?: string;
}

const DemoRequestModal: React.FC<DemoRequestModalProps> = ({
  isOpen,
  onClose,
  vendorSlug,
  source,
  reviewId,
  promoterId,
  promoterName,
  buttonText,
}) => {
  const { t } = useTranslation();
  const resolvedButtonText = buttonText ?? t('shared.demoModal.defaultButton');
  const [createDemoRequest, { isLoading, isSuccess, isError, error }] =
    useCreateDemoRequestMutation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('shared.demoModal.fullNameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('shared.demoModal.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('shared.demoModal.emailInvalid');
    }

    if (!formData.company.trim()) {
      newErrors.company = t('shared.demoModal.companyRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    setErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const requestData: CreateDemoRequestDto = {
      vendorSlug,
      name: formData.name.trim(),
      email: formData.email.trim(),
      company: formData.company.trim(),
      phone: formData.phone.trim() || undefined,
      message: formData.message.trim() || undefined,
      source,
      reviewId,
      promoterId,
      promoterName,
    };

    try {
      await createDemoRequest(requestData).unwrap();
    } catch (err) {
      console.error('Failed to submit demo request:', err);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
    setTimeout(resetForm, 300);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
        {isSuccess ? (
          <div className="p-8 sm:p-10">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>

            <div className="text-center">
              <div className="relative mx-auto mb-6 w-20 h-20">
                <div className="absolute inset-0 bg-green-100 rounded-full animate-ping opacity-30" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-200">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('shared.demoModal.successHeading')}
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {t('shared.demoModal.successBody')}
              </p>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800 font-medium">
                  {t('shared.demoModal.whatsNext')}
                </p>
                <ul className="mt-2 space-y-1.5 text-sm text-blue-700 text-left">
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      1
                    </span>
                    {t('shared.demoModal.step1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      2
                    </span>
                    {t('shared.demoModal.step2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      3
                    </span>
                    {t('shared.demoModal.step3')}
                  </li>
                </ul>
              </div>

              <button
                onClick={handleClose}
                className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
              >
                {t('shared.demoModal.done')}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <Phone className="w-4.5 h-4.5 text-green-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {resolvedButtonText}
                </h2>
              </div>
              <button
                onClick={handleClose}
                disabled={isLoading}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <p className="text-gray-500 text-sm">
                {t('shared.demoModal.fillDetails')}
              </p>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('shared.demoModal.fullName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.name
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    placeholder={t('shared.demoModal.fullNamePlaceholder')}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-600 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('shared.demoModal.emailAddress')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    placeholder={t('shared.demoModal.emailPlaceholder')}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-600 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('shared.demoModal.phoneNumber')}
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 hover:border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                    placeholder={t('shared.demoModal.phonePlaceholder')}
                  />
                </div>
              </div>

              {/* Company Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('shared.demoModal.companyName')} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={`w-full pl-10 pr-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                      errors.company
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    placeholder={t('shared.demoModal.companyPlaceholder')}
                  />
                </div>
                {errors.company && (
                  <p className="text-red-600 text-xs mt-1">{errors.company}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  {t('shared.demoModal.message')}
                </label>
                <div className="relative">
                  <MessageSquare className="absolute left-3.5 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 hover:border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors resize-none"
                    placeholder={t('shared.demoModal.messagePlaceholder')}
                  />
                </div>
              </div>

              {isError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-900">
                      {t('shared.demoModal.submitFailed')}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      {error && 'data' in error
                        ? (error.data as { message?: string })?.message ||
                          t('shared.demoModal.tryAgain')
                        : t('shared.demoModal.tryAgain')}
                    </p>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-green-200/50"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('shared.demoModal.submitting')}
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4" />
                    {resolvedButtonText}
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center">
                {t('shared.demoModal.disclaimer')}
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default DemoRequestModal;
